/**
 * Ultimas3DetalhadoReport - Inline display for Últimas 3 Detalhado
 *
 * Layout matches reference Excel:
 * Fixed: Produto, Descrição, Lote, Validade
 * Dynamic: 4 sub-columns per contagem (Sistema, Contada, Falta, Sobra) grouped by date
 */
import React, { useMemo } from 'react';
import { RelatorioGeraisTable } from '../../relatorios-gerais/RelatorioGeraisTable';
import type { Column } from '../../relatorios-gerais/RelatorioGeraisTable';

interface Props {
  data: any[];
  loading: boolean;
  meta: any;
}

export const Ultimas3DetalhadoReport: React.FC<Props> = ({ data, loading, meta }) => {
  // Build columns dynamically based on contagens — 3 sub-cols per count
  const columns: Column[] = useMemo(() => {
    const fixed: Column[] = [
      { key: 'codigo', label: 'Produto', width: '120px', group: 'Produto' },
      { key: 'descricao', label: 'Descrição', width: '250px', group: 'Produto' },
      { key: 'lote', label: 'Lote', width: '120px', group: 'Produto' },
      { key: 'validade', label: 'Validade', width: '100px', group: 'Produto' },
    ];

    const contagens = meta?.contagens || [];
    const dynamic: Column[] = [];
    contagens.forEach((c: any, i: number) => {
      const groupLabel = c.data || `Contagem ${i + 1}`;
      dynamic.push(
        { key: `val_${i}_sistema`, label: 'Sistema', align: 'right' as const, format: 'number' as const, width: '85px', group: groupLabel },
        { key: `val_${i}_contada`, label: 'Contada', align: 'right' as const, format: 'number' as const, width: '85px', group: groupLabel },
        { key: `val_${i}_falta`, label: 'Falta', align: 'right' as const, format: 'number' as const, width: '85px', group: groupLabel },
        { key: `val_${i}_sobra`, label: 'Sobra', align: 'right' as const, format: 'number' as const, width: '85px', group: groupLabel },
      );
    });

    return [...fixed, ...dynamic];
  }, [meta?.contagens]);

  // Flatten data: convert values dict {0: {sistema, contada, falta}} to flat keys
  const flatData = useMemo(() => {
    return data.map(row => {
      const flat: any = { ...row };
      const values = row.values || {};
      Object.keys(values).forEach(k => {
        const v = values[k];
        flat[`val_${k}_sistema`] = v.sistema;
        flat[`val_${k}_contada`] = v.contada;
        flat[`val_${k}_falta`] = v.falta;
        flat[`val_${k}_sobra`] = v.sobra;
      });
      return flat;
    });
  }, [data]);

  return (
    <div className="space-y-3">
      {meta && (
        <div className="text-sm text-gray-600 dark:text-gray-400 flex flex-wrap gap-4">
          <span><span className="font-medium">Hospital:</span> {meta.hospital}</span>
          {meta.total_items != null && <span>{meta.total_items} itens</span>}
        </div>
      )}
      <RelatorioGeraisTable
        columns={columns}
        data={flatData}
        loading={loading}
        showTotals
        emptyMessage="Nenhum item encontrado para as últimas contagens."
      />
    </div>
  );
};
